import { Duration } from './tab.js'

/** A measure width counts as matching the modal width within this fraction. */
const measureWidthTolerance = 0.15
/** Below this share of measures fitting the grid, no durations are emitted. */
const minRhythmConfidence = 0.6
/** Floating-point slack when comparing beat sums. */
const beatEpsilon = 0.01

/**
 * Meters to try, in quarter notes per measure. 4 is simple time. 6 reads a bar of swung eighths or
 * eighth triplets as 12/8 — which is how a shuffle is actually notated — so those figures come out
 * right instead of being silently rounded into a bar that is a quarter short.
 */
const candidateMeters = [4, 6]

export interface RhythmInput {
  /** Column offset where the run begins. */
  runStart: number
  /** Column offsets where a measure begins, ascending, all >= runStart. */
  measureStarts: number[]
  /** Column offset of the end of the last measure. */
  runEnd: number
  /** Column offsets of note onsets, ascending, deduplicated across strings. */
  onsets: number[]
  /**
   * Quarter notes per measure — 4 for 4/4, 3 for 3/4. Named for what it measures: `beatsFor` scores
   * a quarter as 1, so this is not "beats" in the 6/8 sense, and calling it that produced bars of
   * twice the intended length. Defaults to 4. When omitted, both 4 and 6 are tried.
   */
  quarterNotesPerMeasure?: number
}

export interface RhythmResult {
  /** One entry per onset, in the same order. Empty when inference was not confident. */
  durations: Duration[]
  /** 0..1. Below minRhythmConfidence the durations array is empty. */
  confidence: number
  /** The meter that closed, when one did. Undefined when no durations were emitted. */
  meter?: number
}

/** Quarter notes each Duration is worth, for snapping. */
const beatsFor: [Duration, number][] = [
  [Duration.WholeNote, 4],
  [Duration.HalfNote, 2],
  [Duration.QuarterNote, 1],
  [Duration.EightsNote, 0.5],
  [Duration.SixteenthNote, 0.25],
]

const snap = (beats: number): Duration =>
  beatsFor.reduce((best, candidate) => (Math.abs(candidate[1] - beats) < Math.abs(best[1] - beats) ? candidate : best))[0]

const beatsOf = (duration: Duration): number => beatsFor.find(([value]) => value === duration)?.[1] ?? 0

/** The most common value; ties resolve to the larger, which is the more likely full bar. */
const modal = (values: number[]): number => {
  const counts = new Map<number, number>()
  for (const value of values) {
    counts.set(value, (counts.get(value) ?? 0) + 1)
  }
  return [...counts.entries()].reduce((best, entry) =>
    entry[1] > best[1] || (entry[1] === best[1] && entry[0] > best[0]) ? entry : best
  )[0]
}

/**
 * Bar-length fractions a simple-meter measure can actually take. Dyadic only: one third of a 4/4
 * bar is 1⅓ quarter notes, which no member of `Duration` can express — admitting it while refusing
 * triplets was self-contradictory, and it let ragged intros score a confident 1.0.
 */
const simpleRatios = [0.25, 0.5, 1, 2, 4]
/** In compound time a third of a bar is one beat group, so it becomes legal. */
const compoundRatios = [0.25, 1 / 3, 0.5, 1, 2, 4]

const fits = (width: number, modalWidth: number, ratios: number[]): boolean => {
  const ratio = width / modalWidth
  const nearest = ratios.reduce((best, candidate) =>
    Math.abs(candidate - ratio) < Math.abs(best - ratio) ? candidate : best
  )
  return Math.abs(ratio - nearest) / nearest <= measureWidthTolerance
}

/**
 * Meter is a closure property. A measure in 4/4 is not "notes that look about a quarter apart" — it
 * is a set of durations that SUMS to four quarter notes. Width statistics cannot see this: a clean
 * shuffle on a perfectly uniform grid scores confidence 1.0 while every bar comes out 25% short.
 *
 * Every bar the WIDTH GATE counted as full length must close. Those two must use the same
 * definition of "full bar": checking only bars whose length is exactly the meter, while counting
 * anything within `measureWidthTolerance` as evidence, let a padded bar carry a run to confidence
 * 1.0 while being exempt from holding a bar. A 16/18/16/18 run with identical attacks — which is
 * exactly how transcribers pad — emitted 20 durations with every other bar a quarter short.
 *
 * Bars outside the tolerance are pickups or partials and are legitimately not checked. If no bar
 * qualifies there is nothing to verify against, and the run is declined rather than assumed.
 */
const closes = (
  durations: Duration[],
  positions: number[],
  boundaries: number[],
  widths: number[],
  modalWidth: number,
  meter: number
): boolean => {
  let checked = 0
  for (let index = 0; index < widths.length; index += 1) {
    if (Math.abs(widths[index] / modalWidth - 1) > measureWidthTolerance) {
      continue
    }
    checked += 1
    const start = boundaries[index]
    const end = boundaries[index + 1]
    const sum = durations.reduce(
      (total, duration, onsetIndex) =>
        positions[onsetIndex] >= start - beatEpsilon && positions[onsetIndex] < end - beatEpsilon
          ? total + beatsOf(duration)
          : total,
      0
    )
    if (Math.abs(sum - meter) > beatEpsilon) {
      return false
    }
  }
  return checked > 0
}

export const inferRhythm = (input: RhythmInput): RhythmResult => {
  const { runStart, measureStarts, onsets } = input

  if (measureStarts.length < 2 || onsets.length === 0) {
    return { durations: [], confidence: 0 }
  }

  /**
   * The run ends at the last barline, not one past the last note. Ending at the last note gave the
   * final note of every run a sixteenth — and the last note of a passage is the one most likely to
   * be held, so the error was both audible and always in the same direction.
   */
  const rawEnd = Math.max(input.runEnd, measureStarts[measureStarts.length - 1])

  /**
   * Work in CONTENT columns, not raw ones. A barline occupies a column and belongs to neither
   * measure, so every interior measure reads one column wider than its content — a 7% stretch that
   * flips a snap on narrow grids and costs a well-formed run confidence it has earned.
   *
   * Correcting only the widths and leaving the boundaries raw makes the two disagree, and then no
   * measure ever matches the meter exactly. Mapping every column through the same transform is what
   * keeps widths, boundaries and onsets in one coordinate system.
   */
  const barlineColumns = measureStarts.filter(column => column > runStart)
  const toContent = (column: number) => column - barlineColumns.filter(barline => barline < column).length

  const boundaries = [...measureStarts, rawEnd].map(toContent)
  const runEnd = toContent(rawEnd)
  const contentOnsets = onsets.map(toContent)

  const widths = boundaries
    .slice(1)
    .map((end, index) => end - boundaries[index])
    .filter(width => width > 0)
  if (widths.length === 0) {
    return { durations: [], confidence: 0 }
  }

  const modalWidth = modal(widths)

  /**
   * A grid is a bar length that REPEATS. Two measures are not evidence of one: the modal width
   * always fits itself and some fraction will match the other, so 53% of arbitrary two-measure runs
   * once cleared this gate. Two equal bars still qualify — that is a real, if short, grid.
   */
  const fullBars = widths.filter(width => Math.abs(width / modalWidth - 1) <= measureWidthTolerance).length
  if (fullBars < 2) {
    return { durations: [], confidence: 0 }
  }

  const meters = input.quarterNotesPerMeasure ? [input.quarterNotesPerMeasure] : candidateMeters
  let bestConfidence = 0

  for (const meter of meters) {
    const ratios = meter % 3 === 0 ? compoundRatios : simpleRatios
    const confidence = widths.filter(width => fits(width, modalWidth, ratios)).length / widths.length
    bestConfidence = Math.max(bestConfidence, confidence)
    if (confidence < minRhythmConfidence) {
      continue
    }

    const columnsPerQuarter = modalWidth / meter
    const positions = contentOnsets.map(onset => (onset - runStart) / columnsPerQuarter)
    const durations = positions.map((position, index) => {
      const next = index + 1 < positions.length ? positions[index + 1] : (runEnd - runStart) / columnsPerQuarter
      return snap(next - position)
    })
    const boundaryBeats = boundaries.map(boundary => (boundary - runStart) / columnsPerQuarter)

    if (closes(durations, positions, boundaryBeats, widths, modalWidth, meter)) {
      return { durations, confidence, meter }
    }
  }

  // Nothing closed. The spacing does not determine the rhythm, and saying so is the honest answer.
  return { durations: [], confidence: bestConfidence }
}
